import React, { useState, useEffect, useRef } from "react";
import { Product } from "../../types/Product";
import ProductCard from "./card";
import { Chevron } from "../CustomSVG";
import { DARK_BLUE, LIGHT_GRAY, WHITE } from "../../constants/colors";
import { useTranslation } from "react-i18next";
import { hasPermission, PERMISSIONS } from "../../utils/permissions";
import { useAuth } from "../../context/AuthContext";

interface ProductsCarouselProps {
  products: Product[];
  onViewAll?: () => void;
  renderHeader?: boolean;
}

const ProductsCarousel: React.FC<ProductsCarouselProps> = ({
  products,
  onViewAll,
  renderHeader = true,
}) => {
  // console.log("products ===> ", products);
  const { permissions } = useAuth();
  // Check if user has permission to create or edit products
  const canCreateProduct = hasPermission(
    permissions,
    PERMISSIONS.CREATE_PRODUCTS,
  );
  const canEditProduct = hasPermission(permissions, PERMISSIONS.EDIT_PRODUCTS);
  const canDelete = hasPermission(permissions, PERMISSIONS.DELETE_PRODUCTS);
  const canView = hasPermission(permissions, PERMISSIONS.VIEW_PRODUCTS);
  // User can add products if they have either create or edit permission
  const canCreateAddProduct = canCreateProduct || canEditProduct;

  // console.log(canCreateProduct);
  // console.log(canEditProduct);
  // console.log(canDelete);
  // console.log(canView);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [itemsPerView, setItemsPerView] = useState(3);
  const [isHovered, setIsHovered] = useState(false);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setItemsPerView(1);
      } else if (window.innerWidth < 1024) {
        setItemsPerView(2);
      } else if (window.innerWidth < 1280) {
        setItemsPerView(3);
      } else {
        setItemsPerView(4);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const maxIndex = Math.max(0, products.length - itemsPerView);

  const nextSlide = () => {
    if (isAnimating || currentIndex >= maxIndex) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => Math.min(prev + 1, maxIndex));
    setTimeout(() => setIsAnimating(false), 500);
  };

  const prevSlide = () => {
    if (isAnimating || currentIndex <= 0) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
    setTimeout(() => setIsAnimating(false), 500);
  };

  const goToSlide = (index: number) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex(index);
    setTimeout(() => setIsAnimating(false), 500);
  };

  // Touch handlers for mobile swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      nextSlide();
    }
    if (isRightSwipe) {
      prevSlide();
    }

    setTouchStart(0);
    setTouchEnd(0);
  };

  const cardWidth = 100 / itemsPerView;
  const translateX = -(currentIndex * cardWidth);

  return (
    <div
      style={{
        maxWidth: "1700px",
        margin: "0 auto",
        padding: "2rem 1rem",
      }}
    >
      {/* Header */}
      {renderHeader && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "2rem",
            flexWrap: "wrap",
            gap: "1rem",
          }}
        >
          <div>
            <h2
              style={{
                fontSize: "clamp(1.5rem, 4vw, 2rem)",
                fontWeight: "bold",
                color: DARK_BLUE,
                marginBottom: "0.5rem",
              }}
            >
              {t("Featured Products")}
            </h2>
            <p
              style={{
                fontSize: "clamp(0.875rem, 2vw, 1rem)",
                color: "#6b7280",
              }}
            >
              {t("Discover our latest collection")}
            </p>
          </div>

          {/* View All Button */}
          <button
            onClick={() => {
              if (onViewAll) {
                onViewAll();
              } else {
                window.location.href = "/products";
              }
            }}
            style={{
              backgroundColor: "#0d223f",
              color: WHITE,
              padding: "0.75rem 1.5rem",
              borderRadius: "0.5rem",
              border: "0.5px solid #111827",
              cursor: "pointer",
              fontWeight: "600",
              fontSize: "0.875rem",
              transition: "all 0.3s",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#0d223f";
              e.currentTarget.style.color = WHITE;
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow =
                "0 4px 6px -1px rgba(0, 0, 0, 0.1)";
              e.currentTarget.style.transform = "scale(1.1)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#0d223f";
              e.currentTarget.style.color = WHITE;
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            {t("View All")}
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}

      {/* Carousel Container with Side Buttons */}
      <div
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          gap: "1rem",
        }}
      >
        {/* Left Navigation Button - Always Visible on Desktop */}
        {window.innerWidth >= 768 && (
          <button
            onClick={prevSlide}
            disabled={currentIndex === 0}
            aria-label="Previous products"
            style={{
              backgroundColor: currentIndex === 0 ? "#f3f4f6" : DARK_BLUE,
              color: currentIndex === 0 ? LIGHT_GRAY : WHITE,
              padding: "1rem",
              borderRadius: "9999px",
              border: "none",
              cursor: currentIndex === 0 ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              transition: "all 0.3s",
              boxShadow:
                currentIndex === 0
                  ? "none"
                  : "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
              zIndex: 5,
            }}
            onMouseEnter={(e) => {
              if (currentIndex !== 0) {
                e.currentTarget.style.backgroundColor = "#1f2937";
                e.currentTarget.style.transform = "scale(1.1)";
                e.currentTarget.style.boxShadow =
                  "0 10px 15px -3px rgba(0, 0, 0, 0.2)";
              }
            }}
            onMouseLeave={(e) => {
              if (currentIndex !== 0) {
                e.currentTarget.style.backgroundColor = DARK_BLUE;
                e.currentTarget.style.transform = "scale(1)";
                e.currentTarget.style.boxShadow =
                  "0 4px 6px -1px rgba(0, 0, 0, 0.1)";
              }
            }}
          >
            <Chevron isLeft />
          </button>
        )}

        {/* Carousel Container */}
        <div
          ref={carouselRef}
          style={{
            position: "relative",
            overflow: "hidden",
            padding: "1rem 0",
            flex: 1,
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Products Track */}
          <div
            style={{
              display: "flex",
              transition: isAnimating
                ? "transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)"
                : "none",
              transform: `translateX(${translateX}%)`,
              willChange: "transform",
            }}
          >
            {products.map((product, index) => (
              <div
                key={"div" + (product?.id || index)}
                style={{
                  flex: `0 0 ${cardWidth}%`,
                  padding: "0 0.75rem",
                  boxSizing: "border-box",
                  opacity: 0,
                  animation: `slideIn 0.6s ease-out forwards ${index * 0.1}s`,
                }}
              >
                <ProductCard
                  key={product?.id || index}
                  product={product}
                  canAddToCart={true}
                  canAddToFavorite={true}
                  canEdit={canEditProduct}
                  canRate={!canCreateAddProduct}
                  showRating={!!product.ratingCount ? true : false}
                  isShadow={true}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Right Navigation Button - Always Visible on Desktop */}
        {window.innerWidth >= 768 && (
          <button
            onClick={nextSlide}
            disabled={currentIndex >= maxIndex}
            aria-label="Next products"
            style={{
              backgroundColor: currentIndex >= maxIndex ? "#f3f4f6" : DARK_BLUE,
              color: currentIndex >= maxIndex ? LIGHT_GRAY : WHITE,
              padding: "1rem",
              borderRadius: "9999px",
              border: "none",
              cursor: currentIndex >= maxIndex ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              transition: "all 0.3s",
              boxShadow:
                currentIndex >= maxIndex
                  ? "none"
                  : "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
              zIndex: 5,
            }}
            onMouseEnter={(e) => {
              if (currentIndex < maxIndex) {
                e.currentTarget.style.backgroundColor = "#1f2937";
                e.currentTarget.style.transform = "scale(1.1)";
                e.currentTarget.style.boxShadow =
                  "0 10px 15px -3px rgba(0, 0, 0, 0.2)";
              }
            }}
            onMouseLeave={(e) => {
              if (currentIndex < maxIndex) {
                e.currentTarget.style.backgroundColor = DARK_BLUE;
                e.currentTarget.style.transform = "scale(1)";
                e.currentTarget.style.boxShadow =
                  "0 4px 6px -1px rgba(0, 0, 0, 0.1)";
              }
            }}
          >
            <Chevron />
          </button>
        )}
      </div>

      {/* Pagination Dots */}
      {maxIndex > 0 && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "0.5rem",
            marginTop: "2rem",
            flexWrap: "wrap",
          }}
        >
          {Array.from({ length: maxIndex + 1 }).map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
              style={{
                width: index === currentIndex ? "2.5rem" : "0.75rem",
                height: "0.75rem",
                backgroundColor: index === currentIndex ? DARK_BLUE : "#555555",
                border: "none",
                borderRadius: "9999px",
                cursor: "pointer",
                transition: "all 0.3s",
              }}
              onMouseEnter={(e) => {
                if (index !== currentIndex) {
                  e.currentTarget.style.backgroundColor = LIGHT_GRAY;
                }
              }}
              onMouseLeave={(e) => {
                if (index !== currentIndex) {
                  e.currentTarget.style.backgroundColor = "#d1d5db";
                }
              }}
            />
          ))}
        </div>
      )}

      {/* Mobile Navigation Hint */}
      {window.innerWidth < 768 && maxIndex > 0 && (
        <div
          style={{
            textAlign: "center",
            marginTop: "1rem",
            fontSize: "0.875rem",
            color: "#6b7280",
          }}
        >
          {t("← Swipe to browse →")}
        </div>
      )}

      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(30px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-50%) scale(0.9);
          }
          to {
            opacity: 1;
            transform: translateY(-50%) scale(1);
          }
        }

        /* Smooth scroll for reduced motion preference */
        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }

        /* Improved touch feedback */
        @media (hover: none) and (pointer: coarse) {
          button:active {
            transform: scale(0.95) !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ProductsCarousel;
